'use client'

import React, { useRef, useState, useEffect } from 'react'
import Image from 'next/image'
import { Link, usePathname } from '@/i18n/routing'
import type { Footer, Media } from '@/payload-types'
import AnimatedFeatures from '@/components/AnimatedFeatures'
import { useTheme } from '@/providers/Theme'
import { useTranslations } from 'next-intl'
import { useAnimation } from '@/providers/Animation'


const ThemeToggle: React.FC = () => {
  const { theme, setTheme } = useTheme()
  const [isDark, setIsDark] = React.useState(false)

  React.useEffect(() => {
    if (theme === 'dark') setIsDark(true)
    else if (theme === 'light') setIsDark(false)
    else if (typeof window !== 'undefined') setIsDark(window.matchMedia('(prefers-color-scheme: dark)').matches)
  }, [theme])

  return (
    <button
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={`flex items-center justify-between px-3.5 py-2.5 rounded-lg border text-[0.85rem] font-medium transition-all duration-300 ${
        isDark
          ? 'border-[var(--neon-orange)]/40 bg-[var(--neon-orange)]/15 text-white shadow-[0_0_15px_rgba(255,165,0,0.15)] hover:bg-[var(--neon-orange)]/25'
          : 'border-white/15 bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80'
      }`}
    >
      <div className="flex items-center gap-2.5">
        <span className="text-base leading-none opacity-90">
          {isDark ? '🌙' : '☀️'}
        </span>
        <span>{isDark ? 'Dark Mode' : 'Light Mode'}</span>
      </div>
      <div
        className={`w-8 h-4.5 rounded-full relative transition-colors duration-300 ${isDark ? 'bg-[var(--neon-orange)]/60' : 'bg-white/20'}`}
      >
        <div
          className={`absolute top-[2px] w-3.5 h-3.5 rounded-full bg-white transition-all duration-300 shadow-sm ${isDark ? 'left-[1.125rem]' : 'left-0.5'}`}
        />
      </div>
    </button>
  )
}

/* ─── Settings popover (Theme + Animations) ─────────────────────────── */
const SettingsPopover: React.FC = () => {
  const { animationsEnabled, toggleAnimations } = useAnimation()
  const t = useTranslations('footer')
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={ref} className="relative">
      {/* Gear trigger */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Site settings"
        aria-expanded={open}
        className={`inline-flex items-center justify-center w-10 h-10 rounded-full border transition-all duration-300 backdrop-blur-md ${
          open
            ? 'bg-white/20 border-white/40 text-white shadow-[0_0_15px_rgba(255,255,255,0.1)]'
            : 'bg-white/5 dark:bg-black/20 border-white/20 text-white/70 hover:text-white hover:bg-white/10 hover:border-white/30 hover:shadow-[0_4px_12px_rgba(0,0,0,0.2)]'
        }`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transition-transform duration-500 ${open ? 'rotate-90' : 'rotate-0'}`}
        >
          <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>

      {/* Popover panel */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:absolute md:inset-auto md:bottom-14 md:right-0 md:p-0">
          {/* Mobile backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm md:hidden" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          
          <div className="relative z-10 w-[calc(100vw-32px)] max-w-[360px] md:w-64 rounded-2xl border border-white/20 bg-neutral-900/95 dark:bg-[#0a0a0a]/95 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_20px_40px_rgba(0,0,0,0.3)] p-5 flex flex-col gap-5 md:origin-bottom-right animate-in fade-in zoom-in-95 duration-200">
            {/* Mobile Header */}
            <div className="flex md:hidden items-center justify-between pb-2 border-b border-white/10">
              <span className="font-mono text-xs text-white uppercase tracking-widest font-semibold">Settings</span>
              <button 
                onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>

            {/* Theme */}
            <div className="flex flex-col gap-3">
              <span className="font-mono text-[0.7rem] text-white/50 uppercase tracking-[0.12em] font-semibold">
                {t('theme') || 'Theme'}
              </span>
              <ThemeToggle />
            </div>

            {/* Divider */}
            <div className="h-px bg-white/15" />

            {/* Animations */}
            <div className="flex flex-col gap-3">
              <span className="font-mono text-[0.7rem] text-white/50 uppercase tracking-[0.12em] font-semibold">
                Animations
              </span>
              <button
                onClick={toggleAnimations}
                aria-label={animationsEnabled ? 'Disable animations' : 'Enable animations'}
                className={`flex items-center justify-between px-3.5 py-2.5 rounded-lg border text-[0.85rem] font-medium transition-all duration-300 ${
                  animationsEnabled
                    ? 'border-[var(--neon-orange)]/40 bg-[var(--neon-orange)]/15 text-white shadow-[0_0_15px_rgba(255,165,0,0.15)] hover:bg-[var(--neon-orange)]/25'
                    : 'border-white/15 bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <span className="text-base leading-none opacity-90">
                    {animationsEnabled ? '✨' : '🌑'}
                  </span>
                  <span>{animationsEnabled ? 'On' : 'Off'}</span>
                </div>
                <div
                  className={`w-8 h-4.5 rounded-full relative transition-colors duration-300 ${animationsEnabled ? 'bg-[var(--neon-orange)]/60' : 'bg-white/20'}`}
                >
                  <div
                    className={`absolute top-[2px] w-3.5 h-3.5 rounded-full bg-white transition-all duration-300 shadow-sm ${animationsEnabled ? 'left-[1.125rem]' : 'left-0.5'}`}
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Main footer ────────────────────────────────────────────────────── */
export const FooterClient = ({ footer }: { footer: Footer }) => {
  const t = useTranslations('footer')
  const pathname = usePathname()
  const navItems = footer?.navItems || []

  const legalLinks = [
    { href: '/datenschutz', label: t('datenschutz') },
    { href: '/impressum', label: t('impressum') },
    { href: '/agb', label: t('terms') },
    { href: '/faq', label: t('faq') },
    { href: '/cookie-policy', label: t('cookie') },
    { href: '/return-policy', label: t('return') },
    { href: '/disclaimer', label: t('disclaimer') },
  ]

  const noHeaderFooterUrls = ['/create-account', '/login', '/recover-password']
  if (noHeaderFooterUrls.includes(pathname)) return null

  return (
    <footer className="glass-card border-t border-white/20 dark:border-white/10 mt-auto relative">
      {/* Depth overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.06), rgba(0,0,0,0.22))' }}
      />

      <div className="container mx-auto px-4 py-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 items-center text-center md:items-start md:text-left">
          {/* ── Brand ───────────────────────────────────────────── */}
          <div className="flex flex-col items-center md:items-start md:border-r md:border-white/15 md:pr-8">
            <Link href="/" className="inline-block -mt-1">
              <Image
                src="/smoking-logo.svg"
                alt="Smoking Austria Logo"
                width={210}
                height={63}
                className="h-[68px] w-auto mx-auto md:mx-0"
              />
            </Link>

            {footer?.copyright && (
              <p className="text-[0.85rem] text-white/75 dark:text-neutral-400 mt-4 leading-relaxed">
                {footer.copyright}
              </p>
            )}

            <div className="inline-block mt-5">
              <span className="inline-block bg-white/10 px-3 py-1.5 rounded-md text-[0.82rem] text-white/90 border border-white/15 font-medium">
                {t('brand.cashOnly')}
              </span>
            </div>

            <div className="flex flex-col items-center md:items-start gap-1 mt-5">
              <p className="font-semibold text-[0.88rem] text-white/90">
                {t('brand.phoneService')}
              </p>
              <p className="text-[0.78rem] text-white/55">{t('brand.openingHours')}</p>
              <div className="flex flex-col items-center md:items-start gap-0.5 mt-2">
                <a
                  href="tel:+4313419185"
                  className="text-[0.9rem] text-white/85 hover:text-white hover:underline underline-offset-2 transition-all duration-200"
                >
                  +43 (0) 13419185
                </a>
                <a
                  href="mailto:office@stamm-baum.at"
                  className="text-[0.9rem] text-white/85 hover:text-white hover:underline underline-offset-2 transition-all duration-200"
                >
                  office@stamm-baum.at
                </a>
              </div>
            </div>
          </div>

          {/* ── Legal ───────────────────────────────────────────── */}
          <div className="flex flex-col items-center md:items-start md:border-r md:border-white/15 md:pr-8">
            <h4 className="font-mono text-[0.78rem] uppercase tracking-[0.12em] text-white/50 font-semibold mb-5">
              {t('legal')}
            </h4>
            <nav className="flex flex-col items-center md:items-start gap-2.5">
              {legalLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-[0.92rem] font-normal text-white/80 hover:text-white hover:underline underline-offset-2 transition-all duration-200"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* ── Social + Newsletter ─────────────────────────────── */}
          <div className="flex flex-col items-center md:items-end gap-4 md:pl-4">
            <h4 className="font-mono text-[0.78rem] uppercase tracking-[0.12em] text-white/50 font-semibold">
              {t('followUs')}
            </h4>

            {/* Social icons */}
            <div className="flex items-center justify-center md:justify-end gap-2.5">
              {navItems.map((item) => {
                const icon = item?.icon as Media
                return (
                  <Link
                    key={item.id}
                    href={item.link.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={item.link.label}
                    className="inline-flex items-center justify-center p-2.5 rounded-full border border-white/18 hover:border-[var(--neon-orange)] hover:bg-white/15 transition-all duration-200 bg-white/7 dark:bg-black/20 backdrop-blur-sm hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(255,165,0,0.25)]"
                  >
                    <span className="sr-only">{item.link.label}</span>
                    {icon?.url ? (
                      <Image
                        src={icon.url}
                        alt={item.link.label}
                        width={28}
                        height={28}
                        className="w-7 h-7"
                      />
                    ) : (
                      <span className="text-xs">{item.link.label}</span>
                    )}
                  </Link>
                )
              })}
            </div>

            {/* Divider */}
            <div className="w-16 h-px bg-white/12 my-1 mx-auto md:mr-0 md:ml-auto" />

            {/* Newsletter CTA */}
            <Link
              href="/newsletter"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/20 bg-white/8 hover:bg-white/14 hover:border-white/32 text-white/85 hover:text-white text-[0.88rem] font-medium transition-all duration-200 group"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="opacity-70 group-hover:opacity-100 transition-opacity"
              >
                <rect width="20" height="16" x="2" y="4" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
              {t('newsletter') || 'Newsletter'}
            </Link>

            {/* Settings gear */}
            <div className="mt-1">
              <SettingsPopover />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10 py-4 pb-24 md:pb-4 relative z-10">
        <div className="container mx-auto px-4 flex justify-center items-center">
          <span className="text-[0.7rem] text-white/50 flex items-center gap-2">
            <span>🇦🇹</span>
            <span>Designed in Austria</span>
          </span>
        </div>
      </div>
    </footer>
  )
}
